import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, take, tap } from 'rxjs/operators';

import { AuthenticationDataService } from 'app/core/authentication';
import { UrlService } from 'app/core/http/url.service';
import { WrappedNumber } from 'app/core/kh-utils/wrapper-number';
import { STUDENT_ROUTE } from 'app/routing-constants/student-routing.constant';
import { IClassInfo } from 'app/student/models/class-info';
import { IStudentClassInfo } from 'app/student/models/student-class-info';
import { IStudentInfo } from 'app/student/models/student-info';
import { IStudentSkillInfo } from 'app/student/models/student-skill-info';
import { StudentDataService } from 'app/student/services/student-data.service';

@Injectable({
  providedIn: 'root'
})
export class StudentService {
  private readonly location = inject(Location);
  private readonly httpClient = inject(HttpClient);
  private readonly urlService = inject(UrlService);
  private readonly authService = inject(AuthenticationDataService);
  private readonly router = inject(Router);
  private readonly studentDataService = inject(StudentDataService);

  private activeClassIdSubject = new BehaviorSubject('');
  public activeClassId$ = this.activeClassIdSubject.asObservable();
  private classStudentInfo = new BehaviorSubject<IStudentClassInfo | null>(null);
  public classStudentObs = this.classStudentInfo.asObservable();
  private info: IStudentInfo | null = null;
  public numUnviewedNotifications = 0;

  public get activeClassId(): string {
    return this.activeClassIdSubject.value;
  }

  public set activeClassId(classId: string) {
    this.activeClassIdSubject.next(classId);
  }

  public get classStudent(): IStudentClassInfo | null {
    return this.classStudentInfo.value;
  }

  public get studentInfo(): IStudentInfo | null {
    return this.info ? { ...this.info } : null;
  }

  public set studentInfo(info: IStudentInfo | null) {
    this.info = info;
  }

  public receiveClassInfo(classId?: string | null): IClassInfo | undefined {
    const id = classId ?? this.activeClassId;

    return this.info?.Classes.find(({ ClassId }: IClassInfo) => ClassId === id);
  }

  public updateUnviewedNotifications(): void {
    this.getUnviewedNotifications()
      .pipe(
        tap((unviewedNotifications) => this.numUnviewedNotifications = unviewedNotifications),
        first())
      .subscribe();
  }

  public getStudentInfo(): Observable<IStudentInfo> {
    const url = this.urlService.buildServerApiUrl('Student/Info');
    return this.httpClient.get<IStudentInfo>(url).pipe(tap((student: IStudentInfo) => (this.info = student)));
  }

  public getSolveBranch(classId: string, courseId: string, branchId: string, missionId: string): Observable<IStudentSkillInfo> {
    const url = this.urlService.buildServerApiUrl('Student/Skills', classId, courseId, branchId, missionId);

    return this.httpClient.get<IStudentSkillInfo>(url);
  }

  public getClassInfo(classId: string): Observable<IStudentClassInfo> {
    const url = this.urlService.buildServerApiUrl('Student', 'Classes', classId);

    return this.httpClient.get<IStudentClassInfo>(url);
  }

  // TODO: This method later need to be checked for matching with another routes
  public changeClass(classId: string): void {
    const classInfo = this.info?.Classes.find(({ ClassId }) => ClassId === classId);

    this.studentDataService.updateActiveClass(classId)
      .pipe(take(1))
      .subscribe();

    if (!classInfo) {
      this.getStudentInfo()
        .pipe(
          take(1),
          tap((info) => {
            const newClassInfo = this.info?.Classes.find(({ ClassId }) => ClassId === classId);

            if (newClassInfo) {
              this.openHome(classId, newClassInfo);
            }
          })
        )
        .subscribe();
    }
    else {
      this.openHome(classId, classInfo);
    }
  }

  public updateClassStudent(classId: string): void {
    this.studentDataService.getClass(classId)
      .pipe(
        take(1),
        tap((classInfo) => {
          this.classStudentInfo.next(classInfo);
          this.updateUnviewedNotifications();
        })
      ).subscribe();
  }

  public setClassStudent(classStudent: IStudentClassInfo): void {
    this.classStudentInfo.next(classStudent);
    this.updateUnviewedNotifications();
  }

  public updateNumUnviewedNotification(): void {
    if (this.info) {
      this.studentDataService.getNumUnviewedNotification()
        .pipe(
          tap((value) => this.info!.NumUnviewedNotifications = value),
          first()
        )
        .subscribe();
    }
  }

  private openHome(classId: string, classInfo: IClassInfo): void {
    let HomeState: string[];

    const gameShowLaunchPathReg = /student\/[-0-9a-zA-Z]{36}\/game-show\/join$/;
    const isGameShowLaunchPage = gameShowLaunchPathReg.test(this.location.path());

    if (this.authService.isTransient() || classInfo.IsTransient) {
      HomeState = STUDENT_ROUTE.GAME_SHOW.JOIN.route(classId);
    }
    else if (isGameShowLaunchPage) {
      HomeState = STUDENT_ROUTE.GAME_SHOW.JOIN.route(classId);
    }
    else {
      HomeState = STUDENT_ROUTE.HOME.route(classId);
    }

    this.router.navigate(HomeState).catch((error: unknown) => console.error(error));
  }

  private getUnviewedNotifications(): Observable<number> {
    const url = this.urlService.buildServerApiUrl('Student/NumUnviewedNotification');

    return this.httpClient.get<WrappedNumber>(url).pipe(map((unviewedNotifications) => unviewedNotifications.Value));
  }
}
